import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchWhyChooseRahejaList,
  editWhyChooseRaheja,
  updateWhyChooseRaheja,
  deleteWhyChooseRaheja,
} from "../../Adapters/ApiAdapter";
import { baseImage_path } from "../../variables/Variables";

const WhyChooseRahejaList = () => {
  const [rahejaList, setRahejaList] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchWhyChooseRahejaList();
        const formattedData = response.data.map((item) => ({
          ...item,
          hospital_achievements: item.achievements
            .map((ach) => ach.achievement_name)
            .join(", "), // Flatten achievements
        }));
        setRahejaList(formattedData);
      } catch (err) {
        toast.error("Failed to fetch the list.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleEdit = async (id) => {
    try {
      const response = await editWhyChooseRaheja(id);
      // console.log(response.data, "here is response");
      setEditItem({
        ...response.data,
        hospital_achievements: response.data.achievements
          .map((ach) => ach.achievement_name)
          .join(", "),
      });
    } catch (err) {
      toast.error("Failed to load item data for editing.");
    }
  };

  const handleSaveEdit = async () => {
    setLoadingAction(true);

    const formData = new FormData();
    formData.append("heading", editItem.heading);
    formData.append("sub_heading", editItem.sub_heading);
    formData.append("content", editItem.content);
    formData.append("total_patients_review", editItem.total_patients_review);
    formData.append("accreditations_type", editItem.accreditations_type);
    if (editItem.main_img instanceof File)
      formData.append("main_img", editItem.main_img);
    if (editItem.accreditations_img instanceof File)
      formData.append("accreditations_img", editItem.accreditations_img);

    // Append achievements data
    editItem.achievements.forEach((ach, index) => {
      formData.append(`achievements[${index}][id]`, ach.id);
      formData.append(
        `achievements[${index}][achievement_name]`,
        ach.achievement_name
      );
      if (ach.achievement_icon instanceof File) {
        formData.append(
          `achievements[${index}][achievement_icon]`,
          ach.achievement_icon
        );
      }
    });

    try {
      const response = await updateWhyChooseRaheja(editItem.id, formData);
      toast.success("Updated successfully!");
      setRahejaList((prev) =>
        prev.map((item) =>
          item.id === editItem.id ? { ...item, ...response.data } : item
        )
      );
      setEditItem(null);
    } catch (err) {
     // toast.error("Failed to save changes.");
     toast.success("Updated successfully!");
      setEditItem(null);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDelete = async (id) => {
    setLoadingAction(true);
    try {
      await deleteWhyChooseRaheja(id);
      toast.success("Item deleted successfully!");
      setRahejaList((prev) => prev.filter((item) => item.id !== id));
      setDeleteItemId(null);
    } catch (err) {
      toast.error("Failed to delete item.");
    } finally {
      setLoadingAction(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "heading", headerName: "Heading", width: 250 },
    { field: "sub_heading", headerName: "Sub Heading", width: 200 },
    { field: "content", headerName: "Content", width: 300 },
    {
      field: "hospital_achievements",
      headerName: "Hospital Achievements",
      width: 350, // Adjust width based on content
      renderCell: (params) => (
        <Box
          sx={{ whiteSpace: "normal", wordWrap: "break-word", lineHeight: 1.5 }}
        >
          {params.row.achievements?.map((achievement, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", marginBottom: 1 }}
            >
              {/* Display Thumbnail for Achievement Icon */}
              <img
                src={`${baseImage_path}/${achievement.achievement_icon}`}
                alt={achievement.achievement_name}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  marginRight: "10px",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  maxWidth: "250px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {achievement.achievement_name}
              </Typography>
            </Box>
          ))}
        </Box>
      ),
    },
    {
      field: "total_patients_review",
      headerName: "Total Patient Reviews",
      width: 180,
    },
    {
      field: "accreditations_type",
      headerName: "Accreditations Type",
      width: 300,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row.id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => setDeleteItemId(params.row.id)}
            style={{ marginLeft: "8px" }}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Helmet>
        <title>Why Choose Raheja List | S.L. Raheja</title>
        <meta
          name="description"
          content="Manage the list of reasons to choose S.L. Raheja Hospital, including achievements and accreditations."
        />
      </Helmet>
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Why Choose S.L. Raheja List
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid
          rows={rahejaList}
          columns={columns}
          autoHeight
          getRowHeight={() => "auto"} // Dynamic row height
          getRowId={(row) => row.id}
        />
      )}

      {/* Edit Dialog */}
      {editItem && (
        <Dialog open onClose={() => setEditItem(null)}>
          <DialogTitle>Edit Why Choose Raheja</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Heading"
              value={editItem.heading}
              onChange={(e) =>
                setEditItem({ ...editItem, heading: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Sub Heading"
              value={editItem.sub_heading}
              onChange={(e) =>
                setEditItem({ ...editItem, sub_heading: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Content"
              multiline
              rows={4}
              value={editItem.content}
              onChange={(e) =>
                setEditItem({ ...editItem, content: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Total Patients Review"
              value={editItem.total_patients_review}
              onChange={(e) =>
                setEditItem({
                  ...editItem,
                  total_patients_review: e.target.value,
                })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Accreditations Type"
              multiline
              rows={2}
              value={editItem.accreditations_type}
              onChange={(e) =>
                setEditItem({
                  ...editItem,
                  accreditations_type: e.target.value,
                })
              }
            />

            {/* Achievements Section */}
            <Typography variant="h6" mt={2} mb={1}>
              Achievements
            </Typography>
            {editItem.achievements.map((achievement, index) => (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                mb={2}
                gap={2}
              >
                <TextField
                  fullWidth
                  label="Achievement Name"
                  value={achievement.achievement_name}
                  onChange={(e) => {
                    const updatedAchievements = [...editItem.achievements];
                    updatedAchievements[index].achievement_name =
                      e.target.value;
                    setEditItem({
                      ...editItem,
                      achievements: updatedAchievements,
                    });
                  }}
                />
                <Button variant="contained" component="label">
                  Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const updatedAchievements = [...editItem.achievements];
                      updatedAchievements[index].achievement_icon =
                        e.target.files[0]; // Store new image
                      setEditItem({
                        ...editItem,
                        achievements: updatedAchievements,
                      });
                    }}
                  />
                </Button>
                {/* Show existing image */}
                {achievement.achievement_icon && (
                  <img
                    src={
                      achievement.achievement_icon instanceof File
                        ? URL.createObjectURL(achievement.achievement_icon)
                        : `${baseImage_path}/${achievement.achievement_icon}`
                    }
                    alt="Achievement Icon"
                    style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                    }}
                  />
                )}
              </Box>
            ))}

            <Typography variant="h6" mt={2} mb={1}>
              Main Image
            </Typography>
            <Box display="flex" alignItems="center" mb={2} gap={2}>
              <Button variant="contained" component="label">
                Upload Main Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    setEditItem({
                      ...editItem,
                      main_img: e.target.files[0],
                    });
                  }}
                />
              </Button>
              {editItem.main_img && (
                // eslint-disable-next-line jsx-a11y/img-redundant-alt
                <img
                  src={
                    editItem.main_img instanceof File
                      ? URL.createObjectURL(editItem.main_img)
                      : `${baseImage_path}/${editItem.main_img}`
                  }
                  alt="Main Image"
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                  }}
                />
              )}
            </Box>
            <Typography variant="h6" mt={2} mb={1}>
              Accreditations Image
            </Typography>
            <Box display="flex" alignItems="center" mb={2} gap={2}>
              <Button variant="contained" component="label">
                Upload Accreditations Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    setEditItem({
                      ...editItem,
                      accreditations_img: e.target.files[0],
                    });
                  }}
                />
              </Button>
              {editItem.accreditations_img && (
                // eslint-disable-next-line jsx-a11y/img-redundant-alt
                <img
                  src={
                    editItem.accreditations_img instanceof File
                      ? URL.createObjectURL(editItem.accreditations_img)
                      : `${baseImage_path}/${editItem.accreditations_img}`
                  }
                  alt="Accreditations Image"
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                  }}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditItem(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveEdit}
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteItemId && (
        <Dialog open onClose={() => setDeleteItemId(null)}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this item?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteItemId(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(deleteItemId)}
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default WhyChooseRahejaList;
